import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { certificateAPI } from '../services/api';
import toast from 'react-hot-toast';

const ComprehensiveIssueCertificate = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    studentData: {
      name: '',
      email: '',
      phone: '',
      studentId: '',
      dateOfBirth: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        zipCode: ''
      }
    },
    academicData: {
      degree: '',
      specialization: '',
      duration: '',
      admissionYear: '',
      graduationYear: '',
      overallCGPA: '',
      overallPercentage: '',
      classification: '',
      semesters: [],
      technicalSkills: [],
      softSkills: [],
      certifications: [],
      projects: [],
      internships: [],
      achievements: [],
      thesis: {
        title: '',
        guide: '',
        abstract: '',
        grade: ''
      },
      attendance: {
        overall: '',
        remarks: ''
      },
      disciplinaryRecord: {
        clean: true,
        remarks: ''
      }
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const keys = name.split('.');
    
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = type === 'checkbox' ? checked : value;
      return newData;
    });

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      academicData: {
        ...prev.academicData,
        [field]: prev.academicData[field].map((item, i) => i === index ? value : item)
      }
    }));
  };

  const addArrayItem = (field, defaultItem = '') => {
    setFormData(prev => ({
      ...prev,
      academicData: {
        ...prev.academicData,
        [field]: [...prev.academicData[field], defaultItem]
      }
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      academicData: {
        ...prev.academicData,
        [field]: prev.academicData[field].filter((_, i) => i !== index)
      }
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.studentData.name) newErrors['studentData.name'] = 'Name is required';
      if (!formData.studentData.email) newErrors['studentData.email'] = 'Email is required';
      if (!formData.studentData.studentId) newErrors['studentData.studentId'] = 'Student ID is required';
    }
    
    if (step === 2) {
      if (!formData.academicData.degree) newErrors['academicData.degree'] = 'Degree is required';
      if (!formData.academicData.duration) newErrors['academicData.duration'] = 'Duration is required';
      if (!formData.academicData.admissionYear) newErrors['academicData.admissionYear'] = 'Admission year is required';
      if (!formData.academicData.graduationYear) newErrors['academicData.graduationYear'] = 'Graduation year is required';
      if (!formData.academicData.overallCGPA) newErrors['academicData.overallCGPA'] = 'Overall CGPA is required';
      if (!formData.academicData.overallPercentage) newErrors['academicData.overallPercentage'] = 'Overall percentage is required';
      if (!formData.academicData.classification) newErrors['academicData.classification'] = 'Classification is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;

    setLoading(true);
    try {
      const response = await certificateAPI.issue(formData);
      
      if (response.data.success) {
        const studentAccess = response.data.data;
        
        // Generate unique API code, QR code, and verification link automatically
        const apiCode = studentAccess.apiCode;
        const qrCodeData = `${window.location.origin}/student-access?code=${apiCode}`;
        const verificationLink = `${window.location.origin}/verify/${studentAccess.certificateId}`;
        
        toast.success(
          <div>
            <p>Certificate issued successfully!</p>
            <p><strong>API Code:</strong> {apiCode}</p>
            <p><strong>QR Code:</strong> Generated automatically</p>
            <p><strong>Verification Link:</strong> Created</p>
          </div>,
          { duration: 10000 }
        );
        navigate('/my-certificates');
      } else {
        toast.error(response.data.message || 'Failed to issue certificate');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to issue certificate');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="step-content">
      <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600' }}>Student Personal Information</h3>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="form-group">
          <label className="form-label">Full Name *</label>
          <input
            type="text"
            name="studentData.name"
            value={formData.studentData.name}
            onChange={handleChange}
            className={`form-input ${errors['studentData.name'] ? 'error' : ''}`}
            placeholder="Enter student's full name"
          />
          {errors['studentData.name'] && <span className="form-error">{errors['studentData.name']}</span>}
        </div>
        
        <div className="form-group">
          <label className="form-label">Student ID *</label>
          <input
            type="text"
            name="studentData.studentId"
            value={formData.studentData.studentId}
            onChange={handleChange}
            className={`form-input ${errors['studentData.studentId'] ? 'error' : ''}`}
            placeholder="Enter student ID"
          />
          {errors['studentData.studentId'] && <span className="form-error">{errors['studentData.studentId']}</span>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="form-group">
          <label className="form-label">Email Address *</label>
          <input
            type="email"
            name="studentData.email"
            value={formData.studentData.email}
            onChange={handleChange}
            className={`form-input ${errors['studentData.email'] ? 'error' : ''}`}
            placeholder="student@email.com"
          />
          {errors['studentData.email'] && <span className="form-error">{errors['studentData.email']}</span>}
        </div>
        
        <div className="form-group">
          <label className="form-label">Phone Number</label>
          <input
            type="tel"
            name="studentData.phone"
            value={formData.studentData.phone}
            onChange={handleChange}
            className="form-input"
            placeholder="Phone number"
          />
        </div>
      </div>

      <div className="form-group mb-4">
        <label className="form-label">Date of Birth</label>
        <input
          type="date"
          name="studentData.dateOfBirth"
          value={formData.studentData.dateOfBirth}
          onChange={handleChange}
          className="form-input"
        />
      </div>

      <h4 style={{ marginBottom: '1rem', fontSize: '1.2rem', fontWeight: '600' }}>Address</h4>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="form-group">
          <label className="form-label">Street</label>
          <input
            type="text"
            name="studentData.address.street"
            value={formData.studentData.address.street}
            onChange={handleChange}
            className="form-input"
            placeholder="Street address"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">City</label>
          <input
            type="text"
            name="studentData.address.city"
            value={formData.studentData.address.city}
            onChange={handleChange}
            className="form-input"
            placeholder="City"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="form-group">
          <label className="form-label">State</label>
          <input
            type="text"
            name="studentData.address.state"
            value={formData.studentData.address.state}
            onChange={handleChange}
            className="form-input"
            placeholder="State"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Country</label>
          <input
            type="text"
            name="studentData.address.country"
            value={formData.studentData.address.country}
            onChange={handleChange}
            className="form-input"
            placeholder="Country"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">ZIP Code</label>
          <input
            type="text"
            name="studentData.address.zipCode"
            value={formData.studentData.address.zipCode}
            onChange={handleChange}
            className="form-input"
            placeholder="ZIP Code"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="step-content">
      <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600' }}>Academic Information</h3>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="form-group">
          <label className="form-label">Degree *</label>
          <input
            type="text"
            name="academicData.degree"
            value={formData.academicData.degree}
            onChange={handleChange}
            className={`form-input ${errors['academicData.degree'] ? 'error' : ''}`}
            placeholder="e.g., Bachelor of Technology"
          />
          {errors['academicData.degree'] && <span className="form-error">{errors['academicData.degree']}</span>}
        </div>
        
        <div className="form-group">
          <label className="form-label">Specialization</label>
          <input
            type="text"
            name="academicData.specialization"
            value={formData.academicData.specialization}
            onChange={handleChange}
            className="form-input"
            placeholder="e.g., Computer Science"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="form-group">
          <label className="form-label">Duration *</label>
          <input
            type="text"
            name="academicData.duration"
            value={formData.academicData.duration}
            onChange={handleChange}
            className={`form-input ${errors['academicData.duration'] ? 'error' : ''}`}
            placeholder="e.g., 4 years"
          />
          {errors['academicData.duration'] && <span className="form-error">{errors['academicData.duration']}</span>}
        </div>
        
        <div className="form-group">
          <label className="form-label">Admission Year *</label>
          <input
            type="number"
            name="academicData.admissionYear"
            value={formData.academicData.admissionYear}
            onChange={handleChange}
            className={`form-input ${errors['academicData.admissionYear'] ? 'error' : ''}`}
            min="1900"
            max="2100"
          />
          {errors['academicData.admissionYear'] && <span className="form-error">{errors['academicData.admissionYear']}</span>}
        </div>
        
        <div className="form-group">
          <label className="form-label">Graduation Year *</label>
          <input
            type="number"
            name="academicData.graduationYear"
            value={formData.academicData.graduationYear}
            onChange={handleChange}
            className={`form-input ${errors['academicData.graduationYear'] ? 'error' : ''}`}
            min="1900"
            max="2100"
          />
          {errors['academicData.graduationYear'] && <span className="form-error">{errors['academicData.graduationYear']}</span>}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="form-group">
          <label className="form-label">Overall CGPA *</label>
          <input
            type="number"
            name="academicData.overallCGPA"
            value={formData.academicData.overallCGPA}
            onChange={handleChange}
            className={`form-input ${errors['academicData.overallCGPA'] ? 'error' : ''}`}
            step="0.01"
            min="0"
            max="10"
            placeholder="e.g., 8.5"
          />
          {errors['academicData.overallCGPA'] && <span className="form-error">{errors['academicData.overallCGPA']}</span>}
        </div>
        
        <div className="form-group">
          <label className="form-label">Overall Percentage *</label>
          <input
            type="number"
            name="academicData.overallPercentage"
            value={formData.academicData.overallPercentage}
            onChange={handleChange}
            className={`form-input ${errors['academicData.overallPercentage'] ? 'error' : ''}`}
            step="0.01"
            min="0"
            max="100"
            placeholder="e.g., 85.5"
          />
          {errors['academicData.overallPercentage'] && <span className="form-error">{errors['academicData.overallPercentage']}</span>}
        </div>
        
        <div className="form-group">
          <label className="form-label">Classification *</label>
          <select
            name="academicData.classification"
            value={formData.academicData.classification}
            onChange={handleChange}
            className={`form-input ${errors['academicData.classification'] ? 'error' : ''}`}
          >
            <option value="">Select Classification</option>
            <option value="First Class with Distinction">First Class with Distinction</option>
            <option value="First Class">First Class</option>
            <option value="Second Class">Second Class</option>
            <option value="Third Class">Third Class</option>
            <option value="Pass">Pass</option>
          </select>
          {errors['academicData.classification'] && <span className="form-error">{errors['academicData.classification']}</span>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="form-group">
          <label className="form-label">Overall Attendance (%)</label>
          <input
            type="number"
            name="academicData.attendance.overall"
            value={formData.academicData.attendance.overall}
            onChange={handleChange}
            className="form-input"
            min="0"
            max="100"
            placeholder="e.g., 95"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Attendance Remarks</label>
          <input
            type="text"
            name="academicData.attendance.remarks"
            value={formData.academicData.attendance.remarks}
            onChange={handleChange}
            className="form-input"
            placeholder="e.g., Excellent attendance"
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="step-content">
      <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600' }}>Skills & Achievements</h3>
      
      <div className="mb-6">
        <h4 style={{ marginBottom: '1rem', fontSize: '1.2rem', fontWeight: '600' }}>Technical Skills</h4>
        {formData.academicData.technicalSkills.map((skill, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={skill}
              onChange={(e) => handleArrayChange('technicalSkills', index, e.target.value)}
              className="form-input flex-1"
              placeholder="e.g., JavaScript, Python, React"
            />
            <button
              type="button"
              onClick={() => removeArrayItem('technicalSkills', index)}
              className="btn btn-secondary"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addArrayItem('technicalSkills')}
          className="btn btn-primary"
        >
          Add Technical Skill
        </button>
      </div>

      <div className="mb-6">
        <h4 style={{ marginBottom: '1rem', fontSize: '1.2rem', fontWeight: '600' }}>Soft Skills</h4>
        {formData.academicData.softSkills.map((skill, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={skill}
              onChange={(e) => handleArrayChange('softSkills', index, e.target.value)}
              className="form-input flex-1"
              placeholder="e.g., Leadership, Communication, Teamwork"
            />
            <button
              type="button"
              onClick={() => removeArrayItem('softSkills', index)}
              className="btn btn-secondary"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addArrayItem('softSkills')}
          className="btn btn-primary"
        >
          Add Soft Skill
        </button>
      </div>

      <div className="mb-6">
        <h4 style={{ marginBottom: '1rem', fontSize: '1.2rem', fontWeight: '600' }}>Achievements</h4>
        {formData.academicData.achievements.map((achievement, index) => (
          <div key={index} className="card mb-4" style={{ padding: '1rem', border: '1px solid #e2e8f0' }}>
            <div className="grid grid-cols-2 gap-4 mb-2">
              <input
                type="text"
                value={achievement.title || ''}
                onChange={(e) => handleArrayChange('achievements', index, { ...achievement, title: e.target.value })}
                className="form-input"
                placeholder="Achievement title"
              />
              <input
                type="text"
                value={achievement.category || ''}
                onChange={(e) => handleArrayChange('achievements', index, { ...achievement, category: e.target.value })}
                className="form-input"
                placeholder="Category (Academic, Sports, etc.)"
              />
            </div>
            <textarea
              value={achievement.description || ''}
              onChange={(e) => handleArrayChange('achievements', index, { ...achievement, description: e.target.value })}
              className="form-input mb-2"
              placeholder="Description"
              rows="2"
            />
            <div className="flex gap-2">
              <input
                type="date"
                value={achievement.date || ''}
                onChange={(e) => handleArrayChange('achievements', index, { ...achievement, date: e.target.value })}
                className="form-input flex-1"
              />
              <button
                type="button"
                onClick={() => removeArrayItem('achievements', index)}
                className="btn btn-secondary"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addArrayItem('achievements', { title: '', description: '', category: '', date: '' })}
          className="btn btn-primary"
        >
          Add Achievement
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="step-content">
      <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600' }}>Final Details</h3>
      
      <div className="mb-6">
        <h4 style={{ marginBottom: '1rem', fontSize: '1.2rem', fontWeight: '600' }}>Thesis/Project (if applicable)</h4>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="form-group">
            <label className="form-label">Thesis Title</label>
            <input
              type="text"
              name="academicData.thesis.title"
              value={formData.academicData.thesis.title}
              onChange={handleChange}
              className="form-input"
              placeholder="Thesis/Project title"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Guide/Supervisor</label>
            <input
              type="text"
              name="academicData.thesis.guide"
              value={formData.academicData.thesis.guide}
              onChange={handleChange}
              className="form-input"
              placeholder="Guide name"
            />
          </div>
        </div>
        
        <div className="form-group mb-4">
          <label className="form-label">Abstract</label>
          <textarea
            name="academicData.thesis.abstract"
            value={formData.academicData.thesis.abstract}
            onChange={handleChange}
            className="form-input"
            rows="3"
            placeholder="Brief abstract of thesis/project"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Grade</label>
          <input
            type="text"
            name="academicData.thesis.grade"
            value={formData.academicData.thesis.grade}
            onChange={handleChange}
            className="form-input"
            placeholder="e.g., A+, Excellent"
          />
        </div>
      </div>

      <div className="mb-6">
        <h4 style={{ marginBottom: '1rem', fontSize: '1.2rem', fontWeight: '600' }}>Disciplinary Record</h4>
        <div className="form-group mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="academicData.disciplinaryRecord.clean"
              checked={formData.academicData.disciplinaryRecord.clean}
              onChange={handleChange}
              className="mr-2"
            />
            Clean disciplinary record
          </label>
        </div>
        
        {!formData.academicData.disciplinaryRecord.clean && (
          <div className="form-group">
            <label className="form-label">Disciplinary Remarks</label>
            <textarea
              name="academicData.disciplinaryRecord.remarks"
              value={formData.academicData.disciplinaryRecord.remarks}
              onChange={handleChange}
              className="form-input"
              rows="3"
              placeholder="Details of disciplinary actions (if any)"
            />
          </div>
        )}
      </div>

      <div className="p-4 mb-4" style={{ backgroundColor: '#f7fafc', borderRadius: '8px' }}>
        <h4 style={{ marginBottom: '0.5rem', fontWeight: '600' }}>Certificate Summary</h4>
        <p><strong>Student:</strong> {formData.studentData.name}</p>
        <p><strong>Degree:</strong> {formData.academicData.degree} {formData.academicData.specialization && `in ${formData.academicData.specialization}`}</p>
        <p><strong>CGPA:</strong> {formData.academicData.overallCGPA} ({formData.academicData.overallPercentage}%)</p>
        <p><strong>Classification:</strong> {formData.academicData.classification}</p>
        <p><strong>Duration:</strong> {formData.academicData.admissionYear} - {formData.academicData.graduationYear}</p>
      </div>
    </div>
  );

  return (
    <div className="comprehensive-issue-certificate">
      <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div className="mb-8">
          <h1 style={{ fontSize: '2rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            Issue Comprehensive Certificate
          </h1>
          <p style={{ color: '#718096' }}>
            Complete academic record with all student details
          </p>
        </div>

        {/* Progress Steps */}
        <div className="steps-indicator mb-8">
          <div className="flex justify-between">
            {[1, 2, 3, 4].map(step => (
              <div
                key={step}
                className={`step ${currentStep >= step ? 'active' : ''}`}
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: currentStep >= step ? '#4f46e5' : '#e2e8f0',
                  color: currentStep >= step ? 'white' : '#718096',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem',
                  fontWeight: '600'
                }}
              >
                {step}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            <span style={{ fontSize: '0.9rem', color: '#718096' }}>Personal</span>
            <span style={{ fontSize: '0.9rem', color: '#718096' }}>Academic</span>
            <span style={{ fontSize: '0.9rem', color: '#718096' }}>Skills</span>
            <span style={{ fontSize: '0.9rem', color: '#718096' }}>Final</span>
          </div>
        </div>

        <div className="card">
          <div className="card-body" style={{ padding: '2rem' }}>
            <form onSubmit={handleSubmit}>
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}

              <div className="flex justify-between mt-8">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="btn btn-secondary"
                    disabled={loading}
                  >
                    Previous
                  </button>
                )}
                
                {currentStep < 4 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn btn-primary ml-auto"
                    disabled={loading}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="btn btn-primary ml-auto"
                    disabled={loading}
                  >
                    {loading ? 'Issuing Certificate...' : 'Issue Certificate'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveIssueCertificate;